import { http, httpPaged, type PagedResponse } from '@/services/http';

/**
 * Customer-facing "Orders" view (legacy `app/EMSL/orders/orders.html`) renders
 * these fields; the shape is preserved here so views can stay typed.
 */
export interface Survey {
  id: number;
  reservation_code?: string;
  emsl_order_id?: string;
  project_name?: string;
  /** Project address line 1. */
  address1?: string;
  submitted_by?: string;
  er_inspector_name?: string;
  created_at?: string;
  template_name?: string;
  /** Present when lab results have been uploaded for the order. */
  lab_results_id?: number | string | null;
  watermarked_at?: string | null;
  photo_download_link?: string | null;
  /** GUID used by the XLS / PDF endpoints. */
  guid?: string;
  report_kit_zip?: string | null;
  [key: string]: unknown;
}

function projectScope(projectId: number | string): string {
  return `projects/${projectId}/surveys`;
}

export interface SurveyDateRange {
  /** ISO 8601. Server uses a default 90-day lookback if neither side is set. */
  since?: string;
  /** ISO 8601. */
  until?: string;
}

function withDateRange(
  base: Record<string, unknown>,
  range: SurveyDateRange | undefined,
): Record<string, unknown> {
  const out = { ...base };
  if (range?.since) out.since = range.since;
  if (range?.until) out.until = range.until;
  return out;
}

export const surveysApi = {
  index(projectId: number | string): Promise<Survey[]> {
    return http<Survey[]>({ url: projectScope(projectId), method: 'get' });
  },

  indexPaged(
    projectId: number | string,
    page: number,
    per: number,
    range?: SurveyDateRange,
  ): Promise<PagedResponse<Survey[]>> {
    return httpPaged<Survey[]>({
      url: projectScope(projectId),
      method: 'get',
      params: withDateRange({ page, per }, range),
    });
  },

  search(projectId: number | string, query: string): Promise<Survey[]> {
    return http<Survey[]>({
      url: `${projectScope(projectId)}/search`,
      method: 'get',
      params: { query },
    });
  },

  searchPaged(
    projectId: number | string,
    query: string,
    page: number,
    per: number,
    range?: SurveyDateRange,
  ): Promise<PagedResponse<Survey[]>> {
    return httpPaged<Survey[]>({
      url: `${projectScope(projectId)}/search`,
      method: 'get',
      params: withDateRange({ query, page, per }, range),
    });
  },

  get(projectId: number | string, surveyId: number | string): Promise<Survey> {
    return http<Survey>({ url: `${projectScope(projectId)}/${surveyId}`, method: 'get' });
  },

  remove(projectId: number | string, surveyId: number | string): Promise<void> {
    return http<void>({ url: `${projectScope(projectId)}/${surveyId}`, method: 'delete' });
  },

  raw(projectId: number | string, surveyId: number | string): Promise<unknown> {
    return http({ url: `${projectScope(projectId)}/${surveyId}/raw`, method: 'get' });
  },

  labResults(projectId: number | string, surveyId: number | string): Promise<unknown> {
    return http({ url: `${projectScope(projectId)}/${surveyId}/results`, method: 'get' });
  },
};
